import { Controller } from "@hotwired/stimulus";
import { compressImage, HeicConversionError } from "../src/image_compressor";

export default class extends Controller {
  static values = { signedUrl: String, heicDecoderUrl: String };
  static targets = ["fileInput", "dropOverlay"];
  static outlets = ["channels"];

  connect() {
    this.dragCounter = 0;
  }

  paste(event) {
    const files = this._filesFromClipboard(event.clipboardData);
    if (files.length === 0) return;

    event.preventDefault();
    files.forEach((file) => this._uploadFile(file));
  }

  dragOver(event) {
    event.preventDefault();
  }

  dragEnter(event) {
    event.preventDefault();
    if (!event.dataTransfer.types.includes("Files")) return;

    this.dragCounter++;
    this.dropOverlayTarget.classList.remove("no-show");
  }

  dragLeave(event) {
    this.dragCounter--;
    if (this.dragCounter === 0) {
      this.dropOverlayTarget.classList.add("no-show");
    }
  }

  drop(event) {
    event.preventDefault();
    this.dragCounter = 0;
    this.dropOverlayTarget.classList.add("no-show");

    const files = Array.from(event.dataTransfer.files);
    files.forEach((file) => this._uploadFile(file));
  }

  _filesFromClipboard(clipboardData) {
    if (clipboardData.files.length > 0) {
      return Array.from(clipboardData.files);
    }

    const files = [];
    for (const item of clipboardData.items) {
      if (item.kind === "file") {
        files.push(item.getAsFile());
      }
    }
    return files;
  }

  async uploadFiles(event) {
    const files = Array.from(event.target.files);
    files.forEach((file) => {
      this._uploadFile(file);
    });
    event.target.value = null;
  }

  async _uploadFile(file) {
    this.fileInputTarget.classList.add("in-progress");

    try {
      file = await this._compress(file);
      if (!file) return;

      const authToken = this.authTokenValue;
      const organizationId = this.organizationIdValue;
      const currentChannelId = this.currentChannelIdValue;
      const timestamp = new Date().getTime();
      const key = `channelItems/${currentChannelId}/${timestamp}/${encodeURIComponent(file.name)}`;

      const request = await fetch(this.signedUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authToken,
        },
        body: JSON.stringify({ key: key, contentType: file.type }),
      });
      const response = await request.json();
      const signedUrl = response.uploadURL;

      const upload = await fetch(signedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
          "Content-Length": file.size,
        },
        body: file,
      });
      this.dispatch("itemUploaded", { detail: { file: file, key: key } });
    } catch {
      alert("There was an error uploading the file. Please try a smaller file.");
    } finally {
      this.fileInputTarget.classList.remove("in-progress");
    }
  }

  async _compress(file) {
    try {
      return await compressImage(file, { heicDecoderUrl: this.heicDecoderUrlValue });
    } catch (error) {
      if (!(error instanceof HeicConversionError)) return file;

      alert("This photo couldn't be converted to JPEG. Please convert it and try again.");
    }
  }

  get authTokenValue() {
    return this.channelsOutlet.authTokenValue;
  }

  get currentChannelIdValue() {
    return this.channelsOutlet.currentChannelIdValue;
  }

  get organizationIdValue() {
    return this.channelsOutlet.organizationIdValue;
  }
}
